import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { confirm } from "../utils/confirm";
import { esportaXLSX } from "../utils/esportaXLSX";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { messaggioErrore } from "../utils/erroriUtente";
import PrimoAccessoSezione from "../components/PrimoAccessoSezione";
import ODADetailModal, { STATI_ODA, STATO_ODA_COLORI, calcolaTotaleODA } from "../components/ODADetailModal";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { useCanSee } from "../hooks/useCanSee";
import { Plus, X, ShoppingCart, FileText, Building2, Check, MessageSquare, Send } from "lucide-react";
import { formatValutaItaliana } from "../utils/clienteHelpers";
import { toLocalDateStr } from "../utils/dateUtils";
import { articoliValidi, statoNf, STATI_NF, STATO_NF_COLORI, haOdaValido, nfModificabileDa } from "../utils/acquisti";
import { prendiInGestione } from "../utils/azioniNF";
import { creaOdaDaNf as creaOdaUtil, prossimoNumero } from "../utils/azioniAcquisti";
import RdoTab from "../components/RdoTab";
import RichiediOfferteNF from "../components/RichiediOfferteNF";
import DettaglioNF, { BadgeStatoNF, PulsanteCopiaNF } from "../components/DettaglioNF";

// ─── CELLA INLINE ───────────────────────────────────────────────────────
function CellaInline({ value, type="text", options, onSave, onCancel, placeholder="" }) {
  const [val, setVal] = useState(value||"");
  const ref = useRef();
  useEffect(()=>{ ref.current?.focus(); },[]);

  const save = useCallback(()=>onSave(val), [val, onSave]);
  const handleKey = e => {
    if (e.key==="Enter") save();
    if (e.key==="Escape") onCancel();
  };

  // Testo su più righe (descrizione della NF, W435): Invio va a capo, Ctrl+Invio salva
  if (type === "textarea") return (
    <div style={{display:"flex",gap:3,alignItems:"flex-start"}}>
      <textarea ref={ref} value={val} onChange={e=>setVal(e.target.value)} onBlur={save} rows={Math.min(8, Math.max(3, String(val).split("\n").length))}
        onKeyDown={e=>{ if(e.key==="Escape") onCancel(); if(e.key==="Enter" && (e.ctrlKey||e.metaKey)) save(); }}
        style={{flex:1,padding:"3px 5px",border:"2px solid #7c3aed",borderRadius:5,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none",minWidth:0,resize:"vertical"}}/>
      <button onMouseDown={e=>{e.preventDefault();save();}} title="Salva (Ctrl+Invio)"
        style={{background:"#7c3aed",border:"none",borderRadius:4,padding:"3px 5px",cursor:"pointer",color:"#fff",flexShrink:0}}>
        <Check size={10}/>
      </button>
    </div>
  );
  if (options) return (
    <select ref={ref} value={val}
      onChange={e=>{ setVal(e.target.value); onSave(e.target.value); }}
      onBlur={onCancel} onKeyDown={handleKey}
      style={{width:"100%",padding:"3px 5px",border:"2px solid #7c3aed",borderRadius:5,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none",background:"#fff"}}>
      <option value="">—</option>
      {options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
    </select>
  );

  return (
    <div style={{display:"flex",gap:3,alignItems:"center"}}>
      <input ref={ref} type={type} value={val} onChange={e=>setVal(e.target.value)}
        onBlur={save} onKeyDown={handleKey} placeholder={placeholder}
        style={{flex:1,padding:"3px 5px",border:"2px solid #7c3aed",borderRadius:5,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none",minWidth:0}}/>
      <button onMouseDown={e=>{e.preventDefault();save();}}
        style={{background:"#7c3aed",border:"none",borderRadius:4,padding:"3px 5px",cursor:"pointer",color:"#fff",flexShrink:0}}>
        <Check size={10}/>
      </button>
    </div>
  );
}

function Cella({ value, display, canEdit, onEdit, placeholder="—", style={} }) {
  return (
    <div onClick={canEdit?onEdit:undefined}
      style={{padding:"3px 5px",minHeight:26,cursor:canEdit?"pointer":"default",
        color:value?"#111827":"#d1d5db",fontSize:11,...style,borderRadius:4}}
      onMouseOver={e=>{if(canEdit)e.currentTarget.style.background="#f3f4f6";}}
      onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>
      {display||value||placeholder}
    </div>
  );
}

const URGENZA = ["Normale","Media","Urgente"];

// Numero ODA progressivo — contatore dedicato condiviso tra generazione automatica (da NF)
// e creazione manuale, stesso meccanismo già collaudato per la numerazione Fatturazione.
// Preferito a un conteggio della collection intera: più veloce e con una finestra di rischio
// molto più stretta in caso di due creazioni quasi simultanee.
const prossimoNumeroODA = (orgId) => prossimoNumero(orgId, "ODA");
const URGENZA_COLORI = {
  "Normale": { bg:"#d1fae5", text:"#059669" },
  "Media":   { bg:"#fff7ed", text:"#d97706" },
  "Urgente": { bg:"#fee2e2", text:"#dc2626" },
};

// ════════════════════════════════════════════════════════════
// NF — NUOVA FORNITURA (inline)
// ════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════
// CHAT NF — conversazione bilaterale per ogni NF
// ════════════════════════════════════════════════════════════
function ChatNF({ nf, orgId, currentUser, canEditAcquisti, onClose }) {
  const [messaggi, setMessaggi] = useState([]);
  const [testo, setTesto] = useState("");
  const bottomRef = useRef(null);

  useEffect(()=>{
    if(!nf?.id) return;
    const q = query(
      collection(db,"nfChat"),
      where("nfId","==",nf.id),
      where("orgId","==",orgId)
    );
    return onSnapshot(q, snap=>{
      const list = snap.docs.map(d=>({id:d.id,...d.data()}));
      list.sort((a,b)=>(a.createdAt?.toMillis?.()??0)-(b.createdAt?.toMillis?.()??0));
      setMessaggi(list);
    });
  },[nf?.id, orgId]);

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[messaggi]);

  const invia = async () => {
    if(!testo.trim()) return;
    await addDoc(collection(db,"nfChat"),{
      nfId: nf.id,
      orgId,
      testo: testo.trim(),
      uid: currentUser.uid,
      nomeUtente: currentUser.displayName||"Utente",
      createdAt: serverTimestamp(),
    });
    setTesto("");
    // Aggiorna contatore messaggi non letti sulla NF
    await updateDoc(doc(db,"nuoveForniture",nf.id),{
      ultimoMessaggio: testo.trim(),
      ultimoMessaggioAt: serverTimestamp(),
      ultimoMessaggioUid: currentUser.uid,
    });
  };

  const isMio = (msg) => msg.uid === currentUser?.uid;

  return (
    <div style={{position:"fixed",top:0,right:0,width:360,height:"100vh",background:"#fff",borderLeft:"1px solid #e5e7eb",display:"flex",flexDirection:"column",zIndex:200,boxShadow:"-4px 0 20px rgba(0,0,0,.1)"}}>
      {/* Header */}
      <div style={{background:"#1e1b4b",padding:"14px 16px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <MessageSquare size={15} color="#a5b4fc"/>
            <span style={{fontSize:13,fontWeight:700,color:"#fff"}}>Chat NF</span>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"rgba(255,255,255,.5)",padding:4}}>
            <X size={16}/>
          </button>
        </div>
        <div style={{fontSize:11,color:"#a5b4fc",fontWeight:600}}>{nf.codice||nf.id?.slice(-6).toUpperCase()||"—"} — {nf.descrizione||"Nuova fornitura"}</div>
        <div style={{fontSize:10,color:"rgba(255,255,255,.4)",marginTop:2}}>
          {canEditAcquisti ? "Visibile a te e al richiedente" : "Visibile a te e all'Ufficio Acquisti"}
        </div>
      </div>

      {/* Messaggi */}
      <div style={{flex:1,overflowY:"auto",padding:"12px 14px",display:"flex",flexDirection:"column",gap:8,background:"#f9fafb"}}>
        {messaggi.length===0 && (
          <div style={{textAlign:"center",color:"#9ca3af",fontSize:12,marginTop:40}}>
            <MessageSquare size={32} style={{opacity:.2,display:"block",margin:"0 auto 10px"}}/>
            Nessun messaggio ancora.<br/>Scrivi per iniziare la conversazione.
          </div>
        )}
        {messaggi.map(msg=>(
          <div key={msg.id} style={{display:"flex",flexDirection:"column",alignItems:isMio(msg)?"flex-end":"flex-start"}}>
            {!isMio(msg) && <span style={{fontSize:10,color:"#9ca3af",marginBottom:2,marginLeft:4}}>{msg.nomeUtente}</span>}
            <div style={{
              maxWidth:"80%", padding:"8px 12px", borderRadius:isMio(msg)?"12px 12px 2px 12px":"12px 12px 12px 2px",
              background:isMio(msg)?"#7c3aed":"#fff",
              color:isMio(msg)?"#fff":"#374151",
              fontSize:12, lineHeight:1.5,
              boxShadow:"0 1px 3px rgba(0,0,0,.08)",
              border:isMio(msg)?"none":"1px solid #e5e7eb"
            }}>
              {msg.testo}
            </div>
            <span style={{fontSize:9,color:"#9ca3af",marginTop:2,marginRight:isMio(msg)?2:0,marginLeft:isMio(msg)?0:4}}>
              {msg.createdAt?.toDate?.()?.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})||""}
            </span>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div style={{padding:"10px 12px",borderTop:"1px solid #e5e7eb",background:"#fff",flexShrink:0}}>
        <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
          <textarea value={testo} onChange={e=>setTesto(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();invia();} }}
            placeholder="Scrivi un messaggio... (Invio per inviare)"
            rows={2}
            style={{flex:1,padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",resize:"none",lineHeight:1.4}}/>
          <button onClick={invia} disabled={!testo.trim()}
            style={{padding:"8px 12px",background:testo.trim()?"#7c3aed":"#e5e7eb",border:"none",borderRadius:8,cursor:testo.trim()?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <Send size={15} color={testo.trim()?"#fff":"#9ca3af"}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function NFTab({ orgId, currentUser, canEditAcquisti, onApriOda, onApriRdo }) {
  const [offerteDi, setOfferteDi] = useState(null);
  const [chatNF, setChatNF] = useState(null);
  const [dettaglioId, setDettaglioId] = useState(null);
  const [filtroStato, setFiltroStato] = useState("aperte");
  const { addToast } = useToast();
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const acquisti = canEditAcquisti || isOrgAdmin || isPlatformOwner;
  const [nfs, setNfs] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [cellAttiva, setCellAttiva] = useState(null); // {id, campo}
  // Ordini nati dalle NF (anche quelli creati prima del collegamento esplicito): nfId → ODA.
  // Un ordine annullato non conta: la NF torna «In gestione» e se ne può fare un altro.
  const [odaPerNf, setOdaPerNf] = useState({});
  const [creandoOda, setCreandoOda] = useState(null);
  useEffect(()=>{
    if (!orgId) return;
    return onSnapshot(query(collection(db,"ordiniAcquisto"),where("organizationId","==",orgId)), snap=>{
      const m={};
      snap.docs.forEach(d=>{ const o={id:d.id,...d.data()}; if(o.nfId && !o.eliminato && o.stato!=="Annullato") m[o.nfId]=o; });
      setOdaPerNf(m);
    });
  },[orgId]);

  useEffect(()=>{
    if (!orgId) return;
    const q = query(collection(db,"nuoveForniture"), where("organizationId","==",orgId));
    const u1 = onSnapshot(q, snap=>{
      // Escluse le bozze (mai inviate) e le NF eliminate dal loro autore (prima restavano in elenco)
      const l = snap.docs.map(d=>({id:d.id,...d.data()})).filter(nf=>nf.statoRichiesta!=="Da inviare" && !nf.eliminata);
      l.sort((a,b)=>(b.createdAt?.toMillis?.()||0)-(a.createdAt?.toMillis?.()||0));
      setNfs(l);
    });
    const u2 = onSnapshot(query(collection(db,"fornitori"),where("organizationId","==",orgId)),
      snap=>setFornitori(snap.docs.map(d=>({id:d.id,...d.data()})).filter(f=>!f.eliminato && f.denominazione)
        .sort((a,b)=>a.denominazione.localeCompare(b.denominazione,"it"))));
    return ()=>{u1();u2();};
  },[orgId]);

  // ── Crea l'ODA da una NF (una NF = un ODA valido) ──────────────────────────
  // Pulsante esplicito "Crea ODA": l'ordine nasce già compilato (fornitore, commessa, righe
  // segnate «da NF», consegna, offerta) e NF e ODA si collegano a vicenda.
  // Se l'ordine esiste già non ne crea un secondo: lo apre.
  const creaOdaDaNf = async (nf, { apri = true } = {}) => {
    const esistente = odaPerNf[nf.id];
    if (esistente || haOdaValido(nf)) { if (apri) onApriOda?.(esistente?.id || nf.odaId); return; }
    if (creandoOda) return;
    setCreandoOda(nf.id);
    try {
      const r = await creaOdaUtil(nf, { orgId, utente: currentUser });
      if (!r.esistente) addToast(`ODA ${r.numero} creato dalla NF #${nf.numero||""} — completa prezzi e condizioni`,"success");
      if (apri) onApriOda?.(r.id);
    } catch (err) { addToast(messaggioErrore(err),"error"); }
    finally { setCreandoOda(null); }
  };

  const aggiorna = async (id, campo, valore, nf=null) => {
    const extra = { [campo]:valore, updatedAt:serverTimestamp() };
    // Scrivere nelle colonne dell'ufficio Acquisti prende in gestione la NF (come il pulsante
    // «Prendi in gestione»): da quel momento l'autore non la modifica più.
    const eCampoAcquisti = COLS_ACQ.some(c=>c.key===campo);
    if (eCampoAcquisti && nf && !nf.presaInCaricoDa) {
      await prendiInGestione(nf, currentUser, orgId);
    }
    await updateDoc(doc(db,"nuoveForniture",id), extra);
    setCellAttiva(null);
  };

  const prendi = async (nf) => {
    try { await prendiInGestione(nf, currentUser, orgId); addToast(`NF #${nf.numero||""} presa in gestione`,"success"); }
    catch (e) { addToast(messaggioErrore(e),"error"); }
  };

  // Colonne visibili a tutti — i dati della richiesta, modificabili solo dal suo autore finché è «Da gestire»
  const COLS_BASE = [
    { key:"numero",        label:"N°",             width:50,  readonly:true },
    { key:"statoNf",       label:"Stato",          width:110, readonly:true, special:"stato" },
    { key:"dataRichiesta", label:"Data",           width:90,  type:"date" },
    { key:"fornitoreId",   label:"Fornitore",      width:150, special:"fornitore" },
    { key:"descrizione",   label:"Descrizione",    width:260, type:"textarea" },
    { key:"rifOfferta",    label:"Rif. Offerta",   width:110 },
    { key:"linkOfferta",   label:"Link/PDF Offerta",width:150 },
    { key:"urgenza",       label:"Urgenza",        width:90,  options:URGENZA },
    { key:"compilatoreNome", label:"Richiesta da", width:120, readonly:true },
    { key:"destinazione",  label:"Destinazione",   width:110, readonly:true },
    { key:"commessaDisplay",label:"Commessa",      width:150, special:"commessa", readonly:true },
    { key:"dataConsegna",  label:"Cons. desiderata",width:100, type:"date" },
  ];

  // Colonne riservate ufficio acquisti
  const COLS_ACQ = [
    { key:"noteOrdine",    label:"Note acquisti",  width:170 },
    { key:"noteSolleciti", label:"Note/Solleciti", width:170 },
    { key:"giorni",        label:"Attesa",         width:75,  readonly:true, special:"giorni" },
  ];

  const renderCella = (nf, col) => {
    const isAttiva = cellAttiva?.id===nf.id && cellAttiva?.campo===col.key;
    const soloAcquisti = COLS_ACQ.some(c=>c.key===col.key);
    // Permessi (W435):
    // - colonne viola dell'ufficio Acquisti: chi ha i permessi degli acquisti, finché la NF è aperta;
    // - dati della richiesta: SOLO il suo autore, finché è «Da gestire» (stessa regola di Richiedi NF);
    // - tutti gli altri: sola lettura.
    let editabile = false;
    if (col.readonly) editabile = false;
    else if (soloAcquisti) editabile = acquisti && !nf.annullata;
    else editabile = nfModificabileDa(nf, currentUser?.uid);

    let display = null;
    let value = nf[col.key]||"";

    if (col.special==="stato") display = <BadgeStatoNF nf={nf}/>;
    if (col.special==="giorni") {
      // Giorni tra la richiesta e la presa in gestione (o fino a oggi, se aspetta ancora)
      if (nf.dataRichiesta) {
        const fine = nf.presaInCaricoAt?.toDate?.() || (nf.annullata ? null : new Date());
        if (fine) {
          const diff = Math.max(0, Math.round((fine - new Date(nf.dataRichiesta+"T00:00:00"))/(1000*60*60*24)));
          display = <span title={nf.presaInCaricoAt ? "Giorni prima della presa in gestione" : "Giorni di attesa finora"} style={{fontWeight:700,color:diff>7?"#dc2626":"#059669"}}>{diff}gg{nf.presaInCaricoAt?"":" ⏳"}</span>;
        }
      }
      if (!display) display = <span style={{color:"#d1d5db"}}>—</span>;
    }
    if (col.key==="descrizione") display = (
      <span style={{whiteSpace:"pre-line",display:"-webkit-box",WebkitLineClamp:4,WebkitBoxOrient:"vertical",overflow:"hidden"}}
        title="Apri il dettaglio per leggerla tutta e copiarla">{nf.descrizione}{articoliValidi(nf.articoli).length ? <span style={{display:"block",color:"#6d28d9",fontSize:10}}>📋 {articoliValidi(nf.articoli).length} articoli</span> : null}{nf.rdoRiepilogo ? <span style={{display:"block",color:"#b45309",fontSize:10}}>📨 {nf.rdoRiepilogo}</span> : null}</span>
    );
    if (col.key==="urgenza" && nf.urgenza) {
      const s=URGENZA_COLORI[nf.urgenza]||{};
      display=<span style={{fontSize:10,fontWeight:700,color:s.text,background:s.bg,padding:"2px 6px",borderRadius:10}}>{nf.urgenza}</span>;
    }
    if (col.key==="dataRichiesta"&&nf.dataRichiesta) display=new Date(nf.dataRichiesta+"T00:00:00").toLocaleDateString("it-IT");
    if (col.key==="dataConsegna"&&nf.dataConsegna) display=new Date(nf.dataConsegna+"T00:00:00").toLocaleDateString("it-IT");
    if (col.special==="commessa") { value=nf.commessaNome||nf.commessaLibera||""; display=value||null; }
    if (col.key==="numero") display=<button type="button" onClick={e=>{e.stopPropagation();setDettaglioId(nf.id);}} title="Apri il dettaglio della NF"
      style={{fontSize:11,fontWeight:800,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",padding:0,textDecoration:"underline",fontFamily:"inherit"}}>#{nf.numero}</button>;
    if (col.key==="linkOfferta" && (nf.linkOfferta || nf.allegatoOfferta?.url)) {
      // Un indirizzo scritto senza "http(s)://" sarebbe letto come percorso interno dell'app:
      // si aggiunge "https://" solo per l'apertura, senza toccare il valore scritto.
      const href = nf.linkOfferta ? (/^https?:\/\//i.test(nf.linkOfferta.trim()) ? nf.linkOfferta.trim() : `https://${nf.linkOfferta.trim()}`) : "";
      display = <span style={{display:"flex",gap:8,flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
        {nf.allegatoOfferta?.url && <a href={nf.allegatoOfferta.url} target="_blank" rel="noopener noreferrer" title={nf.allegatoOfferta.nome} style={{fontSize:11,color:"#2563eb",textDecoration:"underline"}}>📎 File</a>}
        {href && <a href={href} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"#2563eb",textDecoration:"underline"}}>🔗 Link</a>}
      </span>;
    }
    if (col.key==="destinazione"&&nf.destinazione==="Misto"&&nf.dettaglioDestinazioneMista) display=<span title={nf.dettaglioDestinazioneMista} style={{borderBottom:"1px dotted #9ca3af",cursor:"help"}}>Misto ℹ️</span>;
    if (col.special==="fornitore") { value=nf.fornitoreId?(nf.fornitoreNome||""):"N.D."; display=value; }

    if (isAttiva) {
      let opts = col.options ? col.options.map(o=>typeof o==="string"?{value:o,label:o}:o) : null;
      // Fornitore: solo dall'anagrafica oppure N.D. — niente testo libero (W435)
      if (col.special==="fornitore") opts = [
        {value:"__nd__",label:"N.D. — lo sceglie l'ufficio Acquisti"},
        ...fornitori.map(f=>({value:f.id,label:f.denominazione})),
      ];
      return (
        <CellaInline value={col.special==="fornitore" ? (nf.fornitoreId||"__nd__") : value} type={col.type||"text"} options={opts}
          onSave={v=>{
            if (col.special==="fornitore") {
              const f=fornitori.find(x=>x.id===v);
              updateDoc(doc(db,"nuoveForniture",nf.id),{ fornitoreId:f?.id||"", fornitoreNome:f?.denominazione||"", updatedAt:serverTimestamp() }).catch(e=>addToast(messaggioErrore(e),"error"));
              setCellAttiva(null);
            } else {
              aggiorna(nf.id,col.key,v,nf).catch(e=>addToast(messaggioErrore(e),"error"));
            }
          }}
          onCancel={()=>setCellAttiva(null)}/>
      );
    }
    return (
      <Cella value={value} display={display} canEdit={editabile}
        onEdit={()=>setCellAttiva({id:nf.id,campo:col.key})}/>
    );
  };

  const ALL_COLS = [...COLS_BASE, ...COLS_ACQ];
  const conteggi = useMemo(()=>{ const c={}; nfs.forEach(n=>{ const s=statoNf(n); c[s]=(c[s]||0)+1; }); return c; },[nfs]);
  const FILTRI = [
    { id:"aperte", label:"Da lavorare", stati:["Da gestire","In gestione"] },
    ...STATI_NF.filter(s=>s!=="Bozza").map(s=>({ id:s, label:s, stati:[s] })),
    { id:"tutte", label:"Tutte", stati:null },
  ];
  const filtro = FILTRI.find(f=>f.id===filtroStato) || FILTRI[0];
  const nfsVisibili = filtro.stati ? nfs.filter(n=>filtro.stati.includes(statoNf(n))) : nfs;
  const nfDettaglio = nfs.find(n=>n.id===dettaglioId) || null;

  return (
    <div style={{padding:16}}>
      <div style={{marginBottom:10}}>
        <div style={{fontSize:14,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
          <FileText size={15} color="#7c3aed"/> Nuove Forniture
          <span style={{fontSize:11,color:"#9ca3af",fontWeight:400}}>· Clicca sul numero per il dettaglio, la copia per il fornitore e le azioni</span>
        </div>
      </div>
      <div role="group" aria-label="Filtra per stato" style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
        {FILTRI.map(f=>{
          const n = f.stati ? f.stati.reduce((s,x)=>s+(conteggi[x]||0),0) : nfs.length;
          const att = f.id===filtroStato;
          const c = f.stati?.length===1 ? STATO_NF_COLORI[f.stati[0]] : null;
          return (
            <button key={f.id} type="button" onClick={()=>setFiltroStato(f.id)} aria-pressed={att}
              style={{fontSize:11.5,fontWeight:att?700:500,padding:"4px 11px",borderRadius:20,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                border:`1px solid ${att?"#7c3aed":"#e5e7eb"}`,background:att?"#7c3aed":(c?c.bg:"#fff"),color:att?"#fff":(c?c.text:"#374151")}}>
              {f.label} ({n})
            </button>
          );
        })}
      </div>

      <div style={{overflowX:"auto",overflowY:"visible",borderRadius:12,border:"1px solid #e5e7eb",WebkitOverflowScrolling:"touch"}}>
        <table style={{borderCollapse:"collapse",fontSize:11,minWidth:1750,tableLayout:"fixed"}}>
          <thead style={{position:"sticky",top:0,zIndex:10}}>
            <tr>
              {COLS_BASE.map(col=>(
                <th key={col.key} style={{padding:"8px 8px",textAlign:"left",fontSize:10,fontWeight:700,color:"#6b7280",
                  background:"#f9fafb",borderBottom:"2px solid #e5e7eb",whiteSpace:"nowrap",width:col.width,minWidth:col.width}}>
                  {col.label}
                </th>
              ))}
              {COLS_ACQ.map((col,i)=>(
                <th key={col.key} style={{padding:"8px 8px",textAlign:"left",fontSize:10,fontWeight:700,
                  color:"#7c3aed",background:"#faf5ff",
                  borderBottom:"2px solid #c4b5fd",
                  borderLeft:i===0?"2px solid #c4b5fd":"none",
                  whiteSpace:"nowrap",width:col.width,minWidth:col.width}}>
                  {i===0 && <span style={{display:"block",fontSize:9,color:"#c4b5fd",marginBottom:2}}>🔒 UFFICIO ACQUISTI</span>}
                  {col.label}
                </th>
              ))}
              <th style={{width:300,background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}/>
            </tr>
          </thead>
          <tbody>
            {nfsVisibili.length===0 ? (
              <tr><td colSpan={ALL_COLS.length+1} style={{padding:40,textAlign:"center",color:"#9ca3af"}}>
                {nfs.length ? "Nessuna NF in questo stato" : "Nessuna NF ricevuta — le richieste arrivano da Personale → Richiedi NF"}
              </td></tr>
            ) : nfsVisibili.map((nf,ri)=>{
              const stato = statoNf(nf);
              const oda = odaPerNf[nf.id];
              return (
                <tr key={nf.id} style={{borderBottom:"1px solid #f3f4f6",
                  background:stato==="Da gestire"?"#fff7f7":ri%2===0?"#fff":"#fafafa",opacity:nf.annullata?.6:1}}>
                  {COLS_BASE.map(col=>(
                    <td key={col.key} style={{padding:"4px 5px",verticalAlign:"top"}}>
                      {renderCella(nf,col)}
                    </td>
                  ))}
                  {COLS_ACQ.map((col,i)=>(
                    <td key={col.key} style={{padding:"4px 5px",background:"#faf5ff",verticalAlign:"top",
                      borderLeft:i===0?"2px solid #c4b5fd":"none"}}>
                      {renderCella(nf,col)}
                    </td>
                  ))}
                  <td style={{padding:"4px 8px",background:"#faf5ff",verticalAlign:"top"}}>
                    <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
                      {acquisti && stato==="Da gestire" && (
                        <button type="button" onClick={()=>prendi(nf)} title="Da questo momento la NF la gestisce l'ufficio Acquisti e chi l'ha fatta non può più modificarla"
                          style={{whiteSpace:"nowrap",fontSize:10.5,fontWeight:700,padding:"3px 8px",borderRadius:6,border:"1px solid #6d28d9",background:"#fff",color:"#6d28d9",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          ✋ Prendi in gestione
                        </button>
                      )}
                      {(oda || haOdaValido(nf)) ? (
                        <button type="button" onClick={()=>onApriOda?.(oda?.id||nf.odaId)} title="Apri l'ordine nato da questa NF"
                          style={{whiteSpace:"nowrap",fontSize:10.5,fontWeight:700,padding:"3px 8px",borderRadius:6,border:"1px solid #a7f3d0",background:"#ecfdf5",color:"#047857",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          🛒 {oda?.numeroODA||nf.odaNumero||"ODA"}{oda?.stato?` · ${oda.stato}`:""}
                        </button>
                      ) : acquisti && !nf.annullata && (
                        <button type="button" onClick={()=>creaOdaDaNf(nf)} disabled={creandoOda===nf.id}
                          title="Crea l'ordine al fornitore già compilato con i dati di questa NF"
                          style={{whiteSpace:"nowrap",fontSize:10.5,fontWeight:700,padding:"3px 8px",borderRadius:6,border:"1px solid #7c3aed",background:"#7c3aed",color:"#fff",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          {creandoOda===nf.id?"…":"🛒 Crea ODA"}
                        </button>
                      )}
                      {!nf.annullata && <PulsanteCopiaNF nf={nf} compatto/>}
                      {nf.modificataDopoInvio && !nf.presaInCaricoDa && (
                        <span title="Il richiedente l'ha modificata dopo l'invio" style={{fontSize:12}}>✏️</span>
                      )}
                      <button onClick={()=>setChatNF(chatNF?.id===nf.id?null:nf)}
                        title="Chat NF" aria-label="Chat NF"
                        style={{background:"none",border:"none",cursor:"pointer",color:nf.ultimoMessaggio?"#7c3aed":"#d1d5db",padding:3,position:"relative"}}
                        onMouseOver={e=>e.currentTarget.style.color="#7c3aed"}
                        onMouseOut={e=>e.currentTarget.style.color=nf.ultimoMessaggio?"#7c3aed":"#d1d5db"}>
                        <MessageSquare size={13}/>
                        {nf.ultimoMessaggio && <span style={{position:"absolute",top:-2,right:-2,width:6,height:6,background:"#7c3aed",borderRadius:"50%"}}/>}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {chatNF && (
        <ChatNF nf={chatNF} orgId={orgId} currentUser={currentUser}
          canEditAcquisti={canEditAcquisti} onClose={()=>setChatNF(null)}/>
      )}
      {nfDettaglio && (
        <DettaglioNF nf={nfDettaglio} orgId={orgId} currentUser={currentUser} acquisti={acquisti}
          onApriOda={(id)=>{ setDettaglioId(null); onApriOda?.(odaPerNf[nfDettaglio.id]?.id||id); }}
          onCreaOda={(n)=>{ setDettaglioId(null); creaOdaDaNf(n); }}
          onRichiediOfferte={(n)=>{ setDettaglioId(null); setOfferteDi(n); }}
          onApriRdo={nfDettaglio.rdoRiepilogo ? (id)=>{ setDettaglioId(null); onApriRdo?.(id); } : null}
          onChiudi={()=>setDettaglioId(null)}/>
      )}
      {offerteDi && <RichiediOfferteNF nf={offerteDi} orgId={orgId} currentUser={currentUser} onChiudi={()=>setOfferteDi(null)} onFatto={()=>onApriRdo?.(offerteDi.id)}/>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ELENCO FORNITORI (inline)
// ════════════════════════════════════════════════════════════
function ElencoFornitoriTab({ orgId, canEditFornitori }) {
  const { addToast } = useToast();
  const [fornitori, setFornitori] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [cellAttiva, setCellAttiva] = useState(null);
  const [nuovaRiga, setNuovaRiga] = useState(false);
  const [fornitoreStorico, setFornitoreStorico] = useState(null);
  const [nuovoForm, setNuovoForm] = useState({ denominazione:"", piva:"", referente:"", telefono:"", cellulare:"", email:"", sconto:"", note:"", noleggiatore:false });

  useEffect(()=>{
    if (!orgId) return;
    const u1 = onSnapshot(query(collection(db,"fornitori"), where("organizationId","==",orgId)), snap=>{
      const l = snap.docs.map(d=>({id:d.id,...d.data()}));
      l.sort((a,b)=>(a.idUnico||999)-(b.idUnico||999));
      setFornitori(l);
    });
    const u2 = onSnapshot(query(collection(db,"ordiniAcquisto"), where("organizationId","==",orgId)),
      snap=>setOrdini(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return ()=>{u1();u2();};
  },[orgId]);

  // Storico prezzi per fornitore — voce per voce, ricavato dalle righe già scritte sugli
  // ODA nel tempo. Nessun listino nuovo da mantenere a parte: i dati esistono già, qui si
  // limita a rileggerli in ordine cronologico per ogni descrizione di riga.
  const storicoPerFornitore = useCallback((fornitoreId)=>{
    const m={};
    ordini.filter(o=>o.fornitoreId===fornitoreId).forEach(o=>{
      (o.righe||[]).forEach(r=>{
        if(!r.descrizione?.trim()||!r.prezzoUnitario) return;
        const k=(r.codice||r.descrizione).trim().toLowerCase();
        if(!m[k]) m[k]={descrizione:r.descrizione,storico:[]};
        m[k].storico.push({data:o.data||"",prezzo:parseFloat(String(r.prezzoUnitario).replace(",","."))||0});
      });
    });
    Object.values(m).forEach(v=>v.storico.sort((a,b)=>(a.data||"").localeCompare(b.data||"")));
    return Object.values(m).filter(v=>v.storico.length>0);
  },[ordini]);


  const nextId = useMemo(()=>{
    if (fornitori.length===0) return 1;
    return Math.max(...fornitori.map(d=>parseInt(d.idUnico)||0))+1;
  },[fornitori]);

  const aggiorna = async (id, campo, valore) => {
    // Controlla duplicato P.IVA se si sta modificando quel campo
    if (campo === "piva" && valore) {
      const pivaClean = valore.trim().replace(/\s/g,"");
      const duplicato = fornitori.find(f => f.id!==id && f.piva?.trim().replace(/\s/g,"")===pivaClean);
      if (duplicato) { addToast(`P.IVA già presente: ${duplicato.denominazione}`,"error"); setCellAttiva(null); return; }
    }
    await updateDoc(doc(db,"fornitori",id),{ [campo]:valore, updatedAt:serverTimestamp() });
    setCellAttiva(null);
  };

  const aggiungiForn = async () => {
    if (!nuovoForm.denominazione.trim()) { addToast("Denominazione obbligatoria","error"); return; }
    if (!nuovoForm.piva?.trim()) { addToast("Partita IVA obbligatoria — serve come identificativo univoco","error"); return; }
    // Controllo duplicato P.IVA
    const pivaClean = nuovoForm.piva.trim().replace(/\s/g,"");
    const duplicato = fornitori.find(f => f.piva?.trim().replace(/\s/g,"") === pivaClean);
    if (duplicato) { addToast(`P.IVA già presente: ${duplicato.denominazione}`, "error"); return; }
    await addDoc(collection(db,"fornitori"),{
      ...nuovoForm, piva:pivaClean,
      idUnico:nextId, organizationId:orgId,
      createdAt:serverTimestamp(), updatedAt:serverTimestamp()
    });
    setNuovaRiga(false);
    setNuovoForm({ denominazione:"", piva:"", referente:"", telefono:"", cellulare:"", email:"", sconto:"", note:"", noleggiatore:false });
    addToast("Fornitore aggiunto ✅","success");
  };

  const COLS = [
    { key:"idUnico",      label:"ID",          width:50,  readonly:true },
    { key:"denominazione",label:"Denominazione *",width:180 },
    // W439: i noleggiatori compaiono per primi quando si apre un noleggio
    { key:"noleggiatore", label:"Noleggiatore", width:90, type:"check" },
    { key:"piva",         label:"P.IVA *",     width:120 },
    { key:"referente",    label:"Referente",   width:130 },
    { key:"telefono",     label:"Telefono",    width:110 },
    { key:"cellulare",    label:"Cellulare",   width:110 },
    { key:"email",        label:"Email",       width:160, type:"email" },
    { key:"sconto",       label:"Sconto %",    width:80 },
    { key:"note",         label:"Note",        width:180 },
  ];

  return (
    <div style={{padding:16}}>
      <div style={{marginBottom:12}}>
        <div style={{fontSize:14,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
          <Building2 size={15} color="#7c3aed"/> Elenco Fornitori
          <span style={{fontSize:11,color:"#9ca3af",fontWeight:400}}>· Clicca su una cella per modificarla</span>
          {canEditFornitori && (
            <button onClick={()=>setNuovaRiga(true)} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:6}}>
              <Plus size={14}/> Nuovo fornitore
            </button>
          )}
        </div>
      </div>
      <div style={{overflowX:"auto",borderRadius:12,border:"1px solid #e5e7eb",WebkitOverflowScrolling:"touch"}}>
        <table style={{borderCollapse:"collapse",fontSize:11,minWidth:1000,tableLayout:"fixed"}}>
          <thead>
            <tr style={{background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}>
              {COLS.map(col=>(
                <th key={col.key} style={{padding:"8px 10px",textAlign:"left",fontSize:10,fontWeight:700,color:"#6b7280",whiteSpace:"nowrap",minWidth:col.width}}>{col.label}</th>
              ))}
              <th style={{width:30}}/>
            </tr>
          </thead>
          <tbody>
            {nuovaRiga && (
              <tr style={{background:"#faf5ff",borderBottom:"1px solid #e9d5ff"}}>
                <td style={{padding:"5px 8px",color:"#9ca3af"}}>#{nextId}</td>
                {COLS.filter(c=>!c.readonly).map(col=>(
                  <td key={col.key} style={{padding:"4px 5px"}}>
                    {col.type==="check" ? (
                      <input type="checkbox" aria-label="Nuovo fornitore: noleggiatore" checked={!!nuovoForm.noleggiatore} onChange={e=>setNuovoForm(f=>({...f,noleggiatore:e.target.checked}))}/>
                    ) : <input className="input" type={col.type||"text"} value={nuovoForm[col.key]||""}
                      onChange={e=>setNuovoForm(f=>({...f,[col.key]:e.target.value}))}
                      placeholder={col.key==="piva"?"P.IVA obbligatoria":""}
                      style={{fontSize:11,padding:"3px 5px",borderColor:col.key==="piva"&&!nuovoForm.piva?"#fca5a5":undefined}}/>}
                  </td>
                ))}
                <td style={{padding:"4px 8px"}}>
                  <div style={{display:"flex",gap:4}}>
                    <button onClick={aggiungiForn} style={{background:"#7c3aed",color:"#fff",border:"none",borderRadius:5,padding:"4px 8px",cursor:"pointer",fontSize:11}}>✓</button>
                    <button onClick={()=>setNuovaRiga(false)} style={{background:"#f3f4f6",color:"#6b7280",border:"none",borderRadius:5,padding:"4px 6px",cursor:"pointer",fontSize:11}}>✕</button>
                  </div>
                </td>
              </tr>
            )}
            {fornitori.length===0 && !nuovaRiga ? (
              <tr><td colSpan={COLS.length+1} style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Nessun fornitore</td></tr>
            ) : fornitori.map((f,ri)=>(
              <tr key={f.id} style={{borderBottom:"1px solid #f3f4f6",background:ri%2===0?"#fff":"#fafafa"}}>
                {COLS.map(col=>{
                  if (col.type==="check") return (
                    <td key={col.key} style={{padding:"4px 5px",textAlign:"center"}}>
                      <input type="checkbox" aria-label={`${f.denominazione}: noleggiatore`} checked={!!f.noleggiatore} disabled={!canEditFornitori}
                        onChange={e=>aggiorna(f.id,"noleggiatore",e.target.checked)}/>
                    </td>
                  );
                  const isAttiva = cellAttiva?.id===f.id&&cellAttiva?.campo===col.key;
                  let display = null;
                  if (col.key==="idUnico") display=<span style={{fontSize:11,fontWeight:700,color:"#fff",background:"#7c3aed",padding:"2px 6px",borderRadius:8}}>#{f.idUnico}</span>;
                  if (col.key==="sconto"&&f.sconto) display=<span style={{fontWeight:700,color:"#059669"}}>{f.sconto}%</span>;
                  return (
                    <td key={col.key} style={{padding:"4px 5px",verticalAlign:"middle"}}>
                      {isAttiva ? (
                        <CellaInline value={f[col.key]||""} type={col.type||"text"}
                          onSave={v=>aggiorna(f.id,col.key,v)}
                          onCancel={()=>setCellAttiva(null)}/>
                      ) : (
                        <Cella value={f[col.key]} display={display} canEdit={canEditFornitori&&!col.readonly}
                          onEdit={()=>setCellAttiva({id:f.id,campo:col.key})}/>
                      )}
                    </td>
                  );
                })}
                <td style={{padding:"4px 5px"}}>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <button onClick={()=>setFornitoreStorico(f)} title="Storico prezzi"
                      style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",padding:3,fontSize:10,fontWeight:700}}>
                      📈
                    </button>
                    {canEditFornitori && (
                      <button onClick={async()=>{ if (await confirm("Eliminare?", { type:"danger" })) await updateDoc(doc(db,"fornitori",f.id),{eliminato:true}); }}
                        style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db",padding:3}}
                        onMouseOver={e=>e.currentTarget.style.color="#dc2626"}
                        onMouseOut={e=>e.currentTarget.style.color="#d1d5db"}>
                        <X size={12}/>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {fornitoreStorico && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setFornitoreStorico(null)}>
          <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:520,maxHeight:"80vh",overflowY:"auto",padding:22}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:15,fontWeight:800}}>Storico prezzi — {fornitoreStorico.denominazione}</div>
              <button onClick={()=>setFornitoreStorico(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280"}}><X size={18}/></button>
            </div>
            {storicoPerFornitore(fornitoreStorico.id).length===0 ? (
              <div style={{color:"#9ca3af",fontSize:13,textAlign:"center",padding:20}}>Nessun prezzo ancora registrato per questo fornitore — comparirà qui non appena verrà creato un ODA con righe.</div>
            ) : storicoPerFornitore(fornitoreStorico.id).map((v,i)=>{
              const ultimo=v.storico[v.storico.length-1];
              const primo=v.storico[0];
              const variazione = primo.prezzo>0 ? ((ultimo.prezzo-primo.prezzo)/primo.prezzo*100) : 0;
              return (
                <div key={i} style={{border:"1px solid #e5e7eb",borderRadius:8,padding:12,marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:6}}>{v.descrizione}</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {v.storico.map((s,j)=>(
                      <span key={j} style={{fontSize:11,background:"#f3f4f6",padding:"3px 9px",borderRadius:8,color:"#374151"}}>
                        {s.data?new Date(s.data+"T00:00:00").toLocaleDateString("it-IT",{month:"2-digit",year:"numeric"}):"—"}: <strong>€ {formatValutaItaliana(s.prezzo)}</strong>
                      </span>
                    ))}
                  </div>
                  {v.storico.length>1 && Math.abs(variazione)>0.5 && (
                    <div style={{fontSize:11,fontWeight:700,marginTop:6,color:variazione>0?"#dc2626":"#059669"}}>
                      {variazione>0?"▲":"▼"} {(Math.abs(variazione)).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}% dal primo al più recente ordine
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ODA — ORDINI DI ACQUISTO (inline + cards)
// ════════════════════════════════════════════════════════════
function ODATab({ orgId, currentUser, commesse, canEditAcquisti, canApprovaOda, canChiudiOda, odaIdIniziale, onOdaIninzialeAperto }) {
  const { addToast } = useToast();
  const [ordini, setOrdini] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [savingOda, setSavingOda] = useState(false);
  const [viewMode, setViewMode] = useState("lista");
  const [odaAperto, setOdaAperto] = useState(null); // documento ODA aperto nel pannello
  const [filtroStato, setFiltroStato] = useState("");

  useEffect(()=>{
    if (!orgId) return;
    const u1=onSnapshot(query(collection(db,"ordiniAcquisto"),where("organizationId","==",orgId)),
      snap=>setOrdini(snap.docs.map(d=>({id:d.id,...d.data()})).filter(o=>!o.eliminato)));
    const u2=onSnapshot(query(collection(db,"fornitori"),where("organizationId","==",orgId)),
      snap=>setFornitori(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return ()=>{u1();u2();};
  },[orgId]);

  // Apertura diretta di un ODA (es. appena creato da una NF, o cliccato dalla NF)
  useEffect(()=>{
    if (!odaIdIniziale) return;
    const o = ordini.find(x=>x.id===odaIdIniziale);
    if (o) { setOdaAperto(o); onOdaIninzialeAperto?.(); }
  },[odaIdIniziale, ordini, onOdaIninzialeAperto]);

  // Tiene aperto lo stesso ODA aggiornato in tempo reale mentre il pannello è visibile
  // (altrimenti dopo un'azione — es. "Approva" — si vedrebbe ancora lo stato vecchio
  // finché non si richiude e riapre il pannello).
  useEffect(()=>{
    if (!odaAperto) return;
    const aggiornato = ordini.find(o=>o.id===odaAperto.id);
    if (aggiornato && aggiornato!==odaAperto) setOdaAperto(aggiornato);
  },[ordini, odaAperto]);

  const nuovoODA = async () => {
    if (savingOda) return;
    setSavingOda(true);
    try {
      const numeroODA = await prossimoNumeroODA(orgId);
      const ref = await addDoc(collection(db,"ordiniAcquisto"),{
        numeroODA, organizationId:orgId, createdBy:currentUser.uid,
        fornitoreId:"", fornitoreNome:"", commessaId:"", commessaNome:"",
        data: toLocalDateStr(new Date()), stato:"Bozza", righe:[],
        createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
      });
      addToast("ODA creato — completa i dati","success");
      setOdaAperto({ id:ref.id, numeroODA, organizationId:orgId, stato:"Bozza", righe:[] });
    } catch(err) {
      addToast(messaggioErrore(err), "error");
    } finally {
      setSavingOda(false);
    }
  };

  const perFornitore = useMemo(()=>{
    const m={};
    ordini.forEach(o=>{
      const k=o.fornitoreId||o.fornitoreNome||"Senza fornitore";
      const nome=o.fornitoreNome||"Senza fornitore";
      const anno=o.data?new Date(o.data).getFullYear():"—";
      const tot=o.importoTotale??calcolaTotaleODA(o.righe);
      if(!m[k]){m[k]={nome,totale:0,perAnno:{}};}
      m[k].totale+=tot;
      m[k].perAnno[anno]=(m[k].perAnno[anno]||0)+tot;
    });
    return Object.values(m).sort((a,b)=>b.totale-a.totale);
  },[ordini]);

  const perCommessa = useMemo(()=>{
    const m={};
    ordini.forEach(o=>{
      const k=o.commessaId||o.commessaLibera||"—";
      const nome=o.commessaNome||o.commessaLibera||"—";
      const anno=o.data?new Date(o.data).getFullYear():"—";
      const tot=o.importoTotale??calcolaTotaleODA(o.righe);
      if(!m[k]){m[k]={nome,totale:0,perAnno:{}};}
      m[k].totale+=tot;
      m[k].perAnno[anno]=(m[k].perAnno[anno]||0)+tot;
    });
    return Object.values(m).sort((a,b)=>b.totale-a.totale);
  },[ordini]);

  const ordiniVisibili = useMemo(()=>{
    let l=[...ordini];
    if (filtroStato) l=l.filter(o=>(o.stato||"Bozza")===filtroStato);
    return l.sort((a,b)=>(b.createdAt?.toMillis()||0)-(a.createdAt?.toMillis()||0));
  },[ordini, filtroStato]);

  return (
    <div style={{padding:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:14,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
          <ShoppingCart size={15} color="#7c3aed"/> Ordini di Acquisto
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {viewMode==="lista" && (
            <select className="input" value={filtroStato} onChange={e=>setFiltroStato(e.target.value)} style={{fontSize:11,padding:"5px 8px"}}>
              <option value="">Tutti gli stati</option>
              {[...STATI_ODA,"Annullato"].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {["lista","fornitori","commesse"].map(v=>(
            <button key={v} onClick={()=>setViewMode(v)}
              style={{padding:"4px 12px",borderRadius:7,border:`1px solid ${viewMode===v?"#7c3aed":"#e5e7eb"}`,
                background:viewMode===v?"#7c3aed":"#fff",color:viewMode===v?"#fff":"#6b7280",
                fontSize:12,fontWeight:viewMode===v?600:400,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              {v==="lista"?"Lista":v==="fornitori"?"Per fornitore":"Per commessa"}
            </button>
          ))}
          {canEditAcquisti && (
            <button onClick={nuovoODA} disabled={savingOda} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:6}}>
              <Plus size={14}/> Nuovo ODA
            </button>
          )}
        </div>
      </div>

      {/* Cards per fornitore/commessa */}
      {viewMode!=="lista" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12,marginBottom:16}}>
          {(viewMode==="fornitori"?perFornitore:perCommessa).map((item,i)=>(
            <div key={i} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16,borderLeft:"4px solid #7c3aed"}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:6}}>{item.nome}</div>
              <div style={{fontSize:20,fontWeight:800,color:"#7c3aed",marginBottom:8}}>€ {formatValutaItaliana(item.totale)}</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {Object.entries(item.perAnno).sort((a,b)=>b[0]-a[0]).map(([anno,val])=>(
                  <span key={anno} style={{fontSize:11,color:"#6b7280",background:"#f3f4f6",padding:"2px 8px",borderRadius:8}}>
                    {anno}: <strong>€ {formatValutaItaliana(val)}</strong>
                  </span>
                ))}
              </div>
            </div>
          ))}
          {(viewMode==="fornitori"?perFornitore:perCommessa).length===0&&(
            <div style={{gridColumn:"1/-1",textAlign:"center",padding:32,color:"#9ca3af",fontSize:13}}>Nessun ordine registrato</div>
          )}
        </div>
      )}

      {/* Tabella lista */}
      {viewMode==="lista" && (
        <div style={{overflowX:"auto",borderRadius:12,border:"1px solid #e5e7eb",WebkitOverflowScrolling:"touch"}}>
          <table style={{borderCollapse:"collapse",fontSize:11,minWidth:900,tableLayout:"fixed"}}>
            <thead>
              <tr style={{background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}>
                {["N° ODA","Data","Fornitore","Commessa","Importo","Stato","Consegna prevista"].map(h=>(
                  <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:10,fontWeight:700,color:"#6b7280",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordiniVisibili.length===0?(
                <tr><td colSpan={7} style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Nessun ODA — clicca "Nuovo ODA" per iniziare</td></tr>
              ):ordiniVisibili.map((o,ri)=>{
                const stato=o.stato||"Bozza";
                const sc=STATO_ODA_COLORI[stato]||{};
                const tot=o.importoTotale??calcolaTotaleODA(o.righe);
                return (
                  <tr key={o.id} onClick={()=>setOdaAperto(o)} style={{borderBottom:"1px solid #f3f4f6",background:ri%2===0?"#fff":"#fafafa",cursor:"pointer"}}
                    onMouseOver={e=>e.currentTarget.style.background="#faf5ff"}
                    onMouseOut={e=>e.currentTarget.style.background=ri%2===0?"#fff":"#fafafa"}>
                    <td style={{padding:"7px 10px",fontWeight:700,color:"#7c3aed",fontFamily:"monospace"}}>{o.numeroODA||o.numeroOda||"—"}</td>
                    <td style={{padding:"7px 10px"}}>{o.data?new Date(o.data+"T00:00:00").toLocaleDateString("it-IT"):"—"}</td>
                    <td style={{padding:"7px 10px"}}>{o.fornitoreNome||<span style={{color:"#d1d5db"}}>— nessuno —</span>}</td>
                    <td style={{padding:"7px 10px"}}>{o.commessaNome||o.commessaLibera||"—"}</td>
                    <td style={{padding:"7px 10px",fontWeight:700,color:"#059669"}}>€ {formatValutaItaliana(tot)}</td>
                    <td style={{padding:"7px 10px"}}><span style={{fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,background:sc.bg,color:sc.text}}>{stato}</span></td>
                    <td style={{padding:"7px 10px"}}>{o.dataConsegnaPrevista?new Date(o.dataConsegnaPrevista+"T00:00:00").toLocaleDateString("it-IT"):"—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {odaAperto && (
        <ODADetailModal oda={odaAperto} orgId={orgId} currentUser={currentUser}
          canEditAcquisti={canEditAcquisti} canApprovaOda={canApprovaOda} canChiudiOda={canChiudiOda}
          fornitori={fornitori} commesse={commesse} onClose={()=>setOdaAperto(null)}/>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PAGINA PRINCIPALE
// ════════════════════════════════════════════════════════════
export default function CentroAcquistiPage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const [activeTab, setActiveTab] = useState("nf");
  const [odaDaAprire, setOdaDaAprire] = useState(null);
  const apriOda = useCallback((id)=>{ if(!id)return; setOdaDaAprire(id); setActiveTab("oda"); },[]);
  const odaAperto = useCallback(()=>setOdaDaAprire(null),[]);
  const [rdoNf, setRdoNf] = useState(null);
  const apriRdo = useCallback((nfId)=>{ setRdoNf(nfId||null); setActiveTab("rdo"); },[]);
  const [commesse, setCommesse] = useState([]);
  const [nfsExport, setNfsExport] = useState([]);
  const canSee = useCanSee();
  const canEditAcquisti = isOrgAdmin || isPlatformOwner || canSee("modifica-acquisti");
  const puoAccedere = isOrgAdmin || isPlatformOwner || canSee("centro-acquisti");
  const canEditFornitori = isOrgAdmin || isPlatformOwner;
  // Separazione dei compiti sull'ODA: chi crea/modifica le righe non è automaticamente
  // chi approva o chi chiude l'ordine. Di default restano riservate a Titolare/Owner —
  // un magazziniere con solo "modifica-acquisti" può oggi comunque compilare, ma serve
  // un secondo permesso esplicito (assegnabile per ruolo o per singola persona in
  // Abilitazioni) per poter approvare o chiudere un ordine.
  const canApprovaOda = isOrgAdmin || isPlatformOwner || canSee("approva-oda");
  const canChiudiOda = isOrgAdmin || isPlatformOwner || canSee("chiudi-oda");

  useEffect(()=>{
    if (!effectiveOrgId) return;
    const u1=onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)),
      snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived && !p.isParent)));
    const u3=onSnapshot(query(collection(db,"nuoveForniture"),where("organizationId","==",effectiveOrgId)),
      snap=>setNfsExport(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return ()=>{u1();u3();};
  },[effectiveOrgId]);

  const TABS = [
    { id:"nf",        label:"📋 NF",               desc:"Nuove Forniture" },
    { id:"rdo",       label:"📨 RdO",              desc:"Richieste d'offerta ai fornitori" },
    { id:"oda",       label:"🛒 ODA",              desc:"Ordini di Acquisto" },
    { id:"fornitori", label:"🏢 Fornitori",         desc:"Elenco Fornitori" },
  ];

  if (!puoAccedere) {
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center"}}>
        <div style={{fontSize:40,marginBottom:12}}>🔒</div>
        <div style={{fontSize:15,fontWeight:700,color:"#374151",marginBottom:6}}>Accesso riservato</div>
        <div style={{fontSize:13,color:"#9ca3af",maxWidth:360}}>Questa sezione è visibile solo a Titolare, Project Manager e Magazziniere.</div>
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px 0",flexShrink:0}}>
        <h2 style={{fontSize:18,fontWeight:700,display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <ShoppingCart size={18} color="#7c3aed"/> NF – ODA – Fornitori
          <button onClick={()=>{
            const intestazione=["Numero","Data","Stato","Descrizione","Articoli","Fornitore","Urgenza","Ordine","Commessa","Richiesta da","In gestione da"];
            const righe=(nfsExport||[]).filter(n=>!n.eliminata && n.statoRichiesta!=="Da inviare").map(n=>[
              n.numero||"—", n.dataRichiesta?new Date(n.dataRichiesta+"T00:00:00").toLocaleDateString("it-IT"):"—", statoNf(n),
              n.descrizione||"—", articoliValidi(n.articoli).map(a=>`${a.quantita||""} ${a.unitaMisura||""} ${a.descrizione}`.trim()).join("\n"),
              n.fornitoreId?(n.fornitoreNome||"—"):"N.D.", n.urgenza||"Normale",
              haOdaValido(n)?`${n.odaNumero||""} · ${n.odaStato||""}`:"—",
              n.commessaNome||"—", n.compilatoreNome||"—", n.presaInCaricoDaNome||"—"
            ]);
            esportaXLSX([intestazione,...righe],`CentroAcquisti_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Acquisti");
          }} style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            📊 Excel
          </button>
        </h2>
        <div style={{display:"flex",gap:4}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{padding:"8px 18px",borderRadius:"8px 8px 0 0",
                border:`1px solid ${activeTab===t.id?"#e5e7eb":"#e5e7eb"}`,
                borderBottom:activeTab===t.id?"1px solid #fff":"1px solid #e5e7eb",
                background:activeTab===t.id?"#fff":"#f9fafb",
                color:activeTab===t.id?"#7c3aed":"#6b7280",
                fontWeight:activeTab===t.id?700:400,cursor:"pointer",
                fontFamily:"'Inter',sans-serif",fontSize:13}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto",overflowX:"hidden"}}>
        <div style={{padding:"14px 20px 0"}}>
          <PrimoAccessoSezione sezioneId="centro-acquisti"/>
        </div>
        {activeTab==="nf"&&<NFTab orgId={effectiveOrgId} currentUser={currentUser} canEditAcquisti={canEditAcquisti} onApriOda={apriOda} onApriRdo={apriRdo}/>}
        {activeTab==="rdo"&&<RdoTab orgId={effectiveOrgId} currentUser={currentUser} canEditAcquisti={canEditAcquisti} nfIniziale={rdoNf} onApriOda={apriOda}/>}
        {activeTab==="oda"&&<ODATab orgId={effectiveOrgId} currentUser={currentUser} commesse={commesse} canEditAcquisti={canEditAcquisti} canApprovaOda={canApprovaOda} canChiudiOda={canChiudiOda} odaIdIniziale={odaDaAprire} onOdaIninzialeAperto={odaAperto}/>}
        {activeTab==="fornitori"&&<ElencoFornitoriTab orgId={effectiveOrgId} canEditFornitori={canEditFornitori}/>}
      </div>
    </div>
  );
}
